import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTACT_INFO } from '../../utils/constants';

interface AttestationReport {
  id: string;
  period: string;
  auditor: string;
  type: string;
  releaseDate: string;
  file: string;
}

const formatPeriod = (period: string, lang: string): string => {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  const locale = lang.startsWith('id') ? 'id-ID' : 'en-US';
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
    .format(new Date(year, month - 1, 1));
};

export const Attestation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [reports, setReports] = useState<AttestationReport[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/atestasi/index.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: { reports: AttestationReport[] }) => {
        if (cancelled) return;
        const sorted = [...(data.reports || [])].sort((a, b) =>
          b.period.localeCompare(a.period),
        );
        setReports(sorted);
      })
      .catch(() => {
        if (!cancelled) setError(t('attestation.error'));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const lang = i18n.language || 'en';

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pt-20">
          <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">
              {t('attestation.badge')}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight bg-gradient-to-r from-gold to-gold-light bg-clip-text text-transparent">
            {t('attestation.title')}
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            {t('attestation.description')}
          </p>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section relative -mt-10 pb-20">
        <div className="container max-w-4xl mx-auto px-4">
          <div className="bg-surface/80 backdrop-blur-md rounded-2xl border border-gold/20 p-8 md:p-12 shadow-xl shadow-gold/5">
            {error ? (
              <div className="text-center py-16">
                <i className="fas fa-exclamation-triangle text-4xl text-gold/60 mb-4"></i>
                <p className="text-gray-400">{error}</p>
              </div>
            ) : reports === null ? (
              <div className="text-center py-16">
                <i className="fas fa-circle-notch fa-spin text-3xl text-gold/60 mb-4"></i>
                <p className="text-gray-400">{t('attestation.loading')}</p>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-16">
                <i className="fas fa-folder-open text-4xl text-gold/60 mb-4"></i>
                <p className="text-gray-400">{t('attestation.empty')}</p>
              </div>
            ) : (
              <div className="relative">
                {/* Vertical line */}
                <div
                  className="absolute left-4 md:left-6 top-2 bottom-2 w-px"
                  style={{ background: 'linear-gradient(to bottom, rgba(245,158,11,0.4), rgba(245,158,11,0.05))' }}
                />

                <ul className="space-y-8">
                  {reports.map((report) => (
                    <li key={report.id} className="relative pl-12 md:pl-16">
                      {/* Dot */}
                      <span
                        className="absolute left-0 md:left-2 top-2 w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          background: 'rgba(245,158,11,0.12)',
                          border: '2px solid rgba(245,158,11,0.6)',
                        }}
                      >
                        <i className="fas fa-file-pdf text-gold text-sm"></i>
                      </span>

                      <div className="modern-card group">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-white mb-1">
                              {t('attestation.reportLabel')} {formatPeriod(report.period, lang)}
                            </h3>
                          </div>
                          <a
                            href={report.file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gold text-black font-semibold text-sm hover:bg-gold-light transition-colors whitespace-nowrap"
                          >
                            <i className="fas fa-download"></i>
                            {t('attestation.downloadButton')}
                          </a>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact CTA */}
            <div className="mt-12 pt-8 border-t border-gold/20 text-center">
              <p className="text-gray-400 mb-3">{t('attestation.ctaQuestion')}</p>
              <a
                href={`mailto:${CONTACT_INFO.EMAIL}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold/5 text-gold-light border border-gold/10 hover:bg-gold/10 transition-colors"
              >
                <i className="fas fa-envelope"></i>
                {t('attestation.ctaContact')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
